import { useState } from 'react'
import { Button, Banner, Loader } from '@cloudflare/kumo'
import { ArrowClockwiseIcon, MoonIcon, SunIcon } from '@phosphor-icons/react'
import { useServers } from './hooks/useServers'
import { useDetail, type Selection } from './hooks/useDetail'
import { KPIStrip } from './components/KPIStrip'
import { FilterBar, EMPTY_FILTERS, type Filters } from './components/FilterBar'
import { HealthGrid, type MatrixSelection } from './components/HealthGrid'
import { DetailDrawer } from './components/DetailDrawer'
import { Legend } from './components/Legend'

function useDarkMode() {
  const [dark, setDark] = useState(
    () => document.documentElement.getAttribute('data-mode') !== 'light'
  )
  const toggle = () => {
    const next = !dark
    setDark(next)
    document.documentElement.setAttribute('data-mode', next ? 'dark' : 'light')
  }
  return { dark, toggle }
}

export function App() {
  const { data, loading, error, lastUpdated, refresh } = useServers()
  const { dark, toggle } = useDarkMode()
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [selection, setSelection] = useState<Selection>(null)
  const [activeCell, setActiveCell] = useState<MatrixSelection | null>(null)

  const detail = useDetail(selection)
  const drawerOpen = selection !== null

  const selectServer = (id: string) => {
    setActiveCell(null)
    setSelection({ kind: 'server', id })
  }
  const selectAccount = (id: string) => {
    setActiveCell(null)
    setSelection({ kind: 'account', id })
  }
  const selectCell = (sel: MatrixSelection) => {
    setActiveCell(sel)
    // A cell drills into the server (outage) view — the primary diagnostic path.
    setSelection({ kind: 'server', id: sel.serverId })
  }
  const closeDrawer = () => {
    setSelection(null)
    setActiveCell(null)
  }

  const blocked = data?.kpis.blocked_connections ?? 0
  const affectedServers = data?.kpis.affected_servers ?? 0

  return (
    <div className="min-h-screen bg-kumo-canvas text-kumo-default">
      <div className="max-w-[1500px] mx-auto px-8 pt-7 pb-16">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-1">
          <div>
            <h1 className="text-2xl font-bold text-kumo-strong">MCP Portal Health</h1>
            <p className="mt-1 text-sm text-kumo-subtle">
              Live status of every server connected to the MCP Portal — filter by
              server or customer to diagnose outages.
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            {lastUpdated && (
              <span className="text-xs text-kumo-subtle">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="secondary"
              icon={<ArrowClockwiseIcon />}
              loading={loading}
              onClick={refresh}
            >
              Refresh
            </Button>
            <Button
              variant="ghost"
              shape="square"
              icon={dark ? <SunIcon /> : <MoonIcon />}
              aria-label="Toggle color mode"
              onClick={toggle}
            />
          </div>
        </div>

        {blocked > 0 && (
          <Banner variant="error" className="mt-4">
            {blocked} blocked connection{blocked === 1 ? '' : 's'} across{' '}
            {affectedServers} server{affectedServers === 1 ? '' : 's'}. Investigate the
            highlighted cells below.
          </Banner>
        )}

        {loading && !data && (
          <div className="py-24 flex justify-center">
            <Loader size={28} />
          </div>
        )}

        {error && !data && (
          <div className="mt-4 rounded-xl border border-kumo-hairline bg-kumo-base py-10 text-center">
            <p className="block text-kumo-danger">Failed to load: {error}</p>
            <div className="mt-3">
              <Button variant="secondary" onClick={refresh}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {data && (
          <>
            <div className="mt-6">
              <KPIStrip kpis={data.kpis} />
            </div>

            <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
              <div className="flex-1 min-w-[300px]">
                <FilterBar
                  filters={filters}
                  onChange={setFilters}
                  servers={data.servers}
                  accounts={data.accounts}
                />
              </div>
              <Legend />
            </div>

            <p className="mb-2.5 block text-xs text-kumo-subtle">
              Servers and customers sorted worst-first. Click a column for a server
              outage view, a row for a customer view, or a cell to drill in.
            </p>

            <HealthGrid
              data={data}
              filters={filters}
              onSelectServer={selectServer}
              onSelectAccount={selectAccount}
              onSelectCell={selectCell}
              activeCell={activeCell}
            />
          </>
        )}

        <DetailDrawer
          open={drawerOpen}
          onClose={closeDrawer}
          loading={detail.loading}
          error={detail.error}
          server={detail.server}
          account={detail.account}
        />
      </div>
    </div>
  )
}
